/**
 * Editor для doc.price_change (Порція Роздріб.5b).
 *
 * Масовий update цін у ref.nomenclature_retail_prices. Items: nomenclature
 * + new_price; old_price + change_percent — обчислюються при POST.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orgs } from '@/api/catalogs';
import { retailPriceChanges } from '@/api/retailPriceChanges';
import { retailPriceGroups } from '@/api/retailPriceGroups';
import { useNumberPreview } from '@/hooks/useNumberPreview';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import RequirePermission from '@/auth/RequirePermission';
import { validateRequired } from '@/utils/validateRequired';
import { extractApiError } from '@/utils/extractApiError';
import RequiredLabel from '@/components/RequiredLabel';
import DatePopover from '@/components/DatePopover';
import NomenclatureSelect from '@/components/NomenclatureSelect';
import { useFormDraft } from '@/shared/hooks/useFormDraft';
import TchTable from '@/shared/components/TchTable';

interface PriceItemRow {
  _key: string;
  line_number: number;
  nomenclature_id: string;
  new_price: string;
  // обчислюється на backend після POST
  old_price?: string | null;
  change_percent?: string | null;
}

interface FormState {
  organization_id: string;
  document_number: string;
  document_date: string;
  price_group_id: string;
  effective_from: string;
  change_reason: string;
  items: PriceItemRow[];
  comment: string;
}

const EMPTY: FormState = {
  organization_id: '',
  document_number: '',
  document_date: '',
  price_group_id: '',
  effective_from: '',
  change_reason: '',
  items: [],
  comment: '',
};

const newItem = (line: number): PriceItemRow => ({
  _key: crypto.randomUUID(),
  line_number: line,
  nomenclature_id: '',
  new_price: '0',
});

export default function PriceChangeEditor() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const { availableOrgs } = usePermissionsByOrg('retail.price_change.create');

  const [form, setForm] = useState<FormState>(EMPTY);
  const [error, setError] = useState<string | null>(null);

  const pgQ = useQuery({
    queryKey: ['retail-price-groups-for-pc', form.organization_id],
    queryFn: () => retailPriceGroups.list({ organization_id: form.organization_id, limit: 500 }),
    enabled: !!form.organization_id,
  });

  const docQuery = useQuery({
    queryKey: ['retail-price-change', id],
    queryFn: () => retailPriceChanges.get(id!),
    enabled: !isNew,
  });

  // Чернетка незбережених правок (2026-08-24): живе у браузері 10 год,
  // у базу нічого не пишеться. Повернувся на документ — запропонує відновити.
  useFormDraft(form, setForm, { ready: isNew || !!docQuery.data });

  const numberPreview = useNumberPreview('doc.price_change', form.organization_id, isNew);

  useEffect(() => {
    if (isNew && availableOrgs.length > 0 && !form.organization_id) {
      setForm((f) => ({ ...f, organization_id: availableOrgs[0]!.id }));
    }
  }, [isNew, availableOrgs, form.organization_id]);

  useEffect(() => {
    if (!isNew && docQuery.data) {
      const d = docQuery.data;
      setForm({
        organization_id: d.organization_id,
        document_number: d.document_number,
        document_date: d.document_date.slice(0, 16),
        price_group_id: d.price_group_id,
        effective_from: d.effective_from.slice(0, 16),
        change_reason: d.change_reason ?? '',
        items: d.items.map((it) => ({
          _key: it.id,
          line_number: it.line_number,
          nomenclature_id: it.nomenclature_id,
          new_price: it.new_price,
          old_price: it.old_price,
          change_percent: it.change_percent,
        })),
        comment: d.comment ?? '',
      });
    }
  }, [isNew, docQuery.data]);

  const posted = !!docQuery.data?.posted;

  const addItem = () => {
    const nextLine = form.items.length > 0 ? Math.max(...form.items.map((i) => i.line_number)) + 1 : 1;
    setForm({ ...form, items: [...form.items, newItem(nextLine)] });
  };

  const removeItem = (key: string) => {
    setForm({ ...form, items: form.items.filter((i) => i._key !== key) });
  };

  const updateItem = (key: string, patch: Partial<PriceItemRow>) => {
    setForm({
      ...form,
      items: form.items.map((i) => (i._key === key ? { ...i, ...patch } : i)),
    });
  };

  const payload = (): Parameters<typeof retailPriceChanges.create>[0] => ({
    organization_id: form.organization_id,
    document_number: form.document_number || null,
    document_date: form.document_date ? new Date(form.document_date).toISOString() : null,
    price_group_id: form.price_group_id,
    effective_from: form.effective_from ? new Date(form.effective_from).toISOString() : null,
    change_reason: form.change_reason || null,
    items: form.items.map(({ _key: _k, old_price: _o, change_percent: _c, ...rest }) => rest),
    comment: form.comment || null,
  });

  const create = useMutation({
    mutationFn: () => retailPriceChanges.create(payload()),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['retail-price-changes'] });
      navigate(`/retail/price-changes/${doc.id}`);
    },
    onError: (e) => setError(extractApiError(e)),
  });

  const update = useMutation({
    mutationFn: () =>
      retailPriceChanges.update(id!, {
        document_number: form.document_number || null,
        document_date: form.document_date ? new Date(form.document_date).toISOString() : null,
        effective_from: form.effective_from ? new Date(form.effective_from).toISOString() : null,
        change_reason: form.change_reason || null,
        items: form.items.map(({ _key: _k, old_price: _o, change_percent: _c, ...rest }) => rest),
        comment: form.comment || null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['retail-price-changes'] });
      qc.invalidateQueries({ queryKey: ['retail-price-change', id] });
    },
    onError: (e) => setError(extractApiError(e)),
  });

  // Новий: створити та провести одним кліком (create → post).
  const createAndPost = useMutation({
    mutationFn: async () => {
      const doc = await retailPriceChanges.create(payload());
      try {
        await retailPriceChanges.post(doc.id);
      } catch (e) {
        // ч.1343: документ уже створено — відкриваємо його, інакше повторне
        // «провести» створило б дубль (правило ч.1266).
        navigate(`/retail/price-changes/${doc.id}`, { replace: true });
        throw e;
      }
      return doc;
    },
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['retail-price-changes'] });
      navigate(`/retail/price-changes/${doc.id}`);
    },
    onError: (e) => setError(extractApiError(e)),
  });

  // Збережений: зберегти правки та провести одним кліком (update → post).
  const updateAndPost = useMutation({
    mutationFn: async () => {
      await retailPriceChanges.update(id!, {
        document_number: form.document_number || null,
        document_date: form.document_date ? new Date(form.document_date).toISOString() : null,
        effective_from: form.effective_from ? new Date(form.effective_from).toISOString() : null,
        change_reason: form.change_reason || null,
        items: form.items.map(({ _key: _k, old_price: _o, change_percent: _c, ...rest }) => rest),
        comment: form.comment || null,
      });
      await retailPriceChanges.post(id!);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['retail-price-changes'] });
      qc.invalidateQueries({ queryKey: ['retail-price-change', id] });
    },
    onError: (e) => setError(extractApiError(e)),
  });

  const unpost = useMutation({
    mutationFn: () => retailPriceChanges.unpost(id!),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['retail-price-change', id] }),
    onError: (e) => setError(extractApiError(e)),
  });

  const remove = useMutation({
    mutationFn: () => retailPriceChanges.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['retail-price-changes'] });
      navigate('/retail/price-changes');
    },
    onError: (e) => setError(extractApiError(e)),
  });

  function validate(): boolean {
    setError(null);
    const err = validateRequired(
      {
        organization_id: form.organization_id,
        price_group_id: form.price_group_id,
      },
      {
        organization_id: 'Юрособа',
        price_group_id: 'Цінова група',
      },
    );
    if (err) { setError(err); return false; }
    if (form.items.length === 0) {
      setError('Додайте позиції перед проведенням');
      return false;
    }
    return true;
  }

  const handleSave = () => {
    if (!validate()) return;
    if (isNew) create.mutate();
    else update.mutate();
  };
  // Новий: зберегти та провести.
  const handleSaveAndPost = () => {
    if (!validate()) return;
    createAndPost.mutate();
  };
  // Збережений: зберегти правки та провести.
  const handleUpdateAndPost = () => {
    if (!validate()) return;
    updateAndPost.mutate();
  };

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew
            ? `Зміна цін ${numberPreview ? `(${numberPreview})` : ''}`
            : `Зміна цін ${form.document_number}`}
          {posted && (
            <span style={{ marginLeft: '0.75rem', color: 'var(--badge-green-fg)', fontSize: '1rem' }}>
              ✓ Проведено
            </span>
          )}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button onClick={() => navigate('/retail/price-changes')}>Назад</button>
          {!posted && (
            <RequirePermission code="retail.price_change.create">
              <button
                className="primary"
                onClick={handleSave}
                disabled={create.isPending || update.isPending || createAndPost.isPending || form.items.length === 0}
              >
                Зберегти
              </button>
            </RequirePermission>
          )}
          {isNew && (
            <RequirePermission code="retail.price_change.post">
              <button
                className="primary"
                onClick={handleSaveAndPost}
                disabled={create.isPending || createAndPost.isPending || form.items.length === 0}
                title="Зберігає та проводить документ"
              >
                ▶ Провести (масовий update)
              </button>
            </RequirePermission>
          )}
          {!isNew && !posted && (
            <RequirePermission code="retail.price_change.post">
              <button
                className="primary"
                onClick={handleUpdateAndPost}
                disabled={updateAndPost.isPending || update.isPending || form.items.length === 0}
                title="Зберігає поточні зміни та проводить документ"
              >
                ▶ Провести (масовий update)
              </button>
            </RequirePermission>
          )}
          {!isNew && posted && (
            <RequirePermission code="retail.price_change.post">
              <button onClick={() => unpost.mutate()} disabled={unpost.isPending}>
                ◀ Скасувати проводку
              </button>
            </RequirePermission>
          )}
          {!isNew && !posted && (
            <RequirePermission code="retail.price_change.create">
              <button
                className="danger"
                onClick={() => {
                  if (window.confirm('Видалити чернетку?')) remove.mutate();
                }}
              >
                Видалити
              </button>
            </RequirePermission>
          )}
        </div>
      </div>

      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <RequiredLabel>Організація</RequiredLabel>
          {isNew ? (
            <select
              value={form.organization_id}
              onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
            >
              <option value="">—</option>
              {availableOrgs.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name} — {o.code}
                </option>
              ))}
            </select>
          ) : (
            <div>
              {orgList.data?.items.find((o) => o.id === form.organization_id)?.name ??
                form.organization_id}
            </div>
          )}
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Дата документу</label>
          <DatePopover
            withTime
            value={form.document_date}
            onChange={(v) => setForm({ ...form, document_date: v })}
            disabled={posted}
          />
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <RequiredLabel>Цінова група</RequiredLabel>
          <select
            value={form.price_group_id}
            onChange={(e) => setForm({ ...form, price_group_id: e.target.value })}
            disabled={!isNew}
          >
            <option value="">—</option>
            {pgQ.data?.items.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name} — {g.code}
              </option>
            ))}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Діє з</label>
          <DatePopover
            withTime
            value={form.effective_from}
            onChange={(v) => setForm({ ...form, effective_from: v })}
            disabled={posted}
          />
        </div>
        <div className="field" style={{ gridColumn: 'span 8' }}>
          <label>Причина зміни (опц.)</label>
          <input
            value={form.change_reason}
            onChange={(e) => setForm({ ...form, change_reason: e.target.value })}
            maxLength={50}
            disabled={posted}
            placeholder="Сезонна знижка, оновлення прайсу..."
          />
        </div>
      </div>

      <h3 style={{ marginTop: '1.5rem' }}>Позиції зміни цін</h3>
      <TchTable>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--border-strong)', background: 'var(--bg-soft)' }}>
            <th style={{ width: 50, textAlign: 'left' }}>#</th>
            <th style={{ textAlign: 'left' }}>Номенклатура</th>
            <th style={{ width: 130, textAlign: 'right' }}>Стара ціна</th>
            <th style={{ width: 130, textAlign: 'right' }}>Нова ціна</th>
            <th style={{ width: 110, textAlign: 'right' }}>Зміна %</th>
            <th style={{ width: 50 }}></th>
          </tr>
        </thead>
        <tbody>
          {form.items.map((it) => (
            <tr key={it._key} style={{ borderBottom: '1px solid var(--border)' }}>
              <td>{it.line_number}</td>
              <td>
                <NomenclatureSelect
                  value={it.nomenclature_id}
                  disabled={posted}
                  onChange={(v) => updateItem(it._key, { nomenclature_id: v })}
                />
              </td>
              <td style={{ textAlign: 'right', color: 'var(--text-secondary)' }}>
                {it.old_price ?? '—'}
              </td>
              <td>
                <input
                  type="number"
                  step="0.01"
                  value={it.new_price}
                  onChange={(e) => updateItem(it._key, { new_price: e.target.value })}
                  disabled={posted}
                  style={{ width: '100%', textAlign: 'right' }}
                />
              </td>
              <td style={{ textAlign: 'right' }}>
                {it.change_percent !== null && it.change_percent !== undefined
                  ? `${Number(it.change_percent).toFixed(2)} %`
                  : '—'}
              </td>
              <td style={{ textAlign: 'center' }}>
                {!posted && (
                  <button onClick={() => removeItem(it._key)} title="Видалити">
                    ×
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </TchTable>
      {!posted && (
        <button onClick={addItem} style={{ marginTop: '0.5rem' }}>
          + Додати позицію
        </button>
      )}

      <div className="field" style={{ marginTop: '1rem' }}>
        <label>Коментар</label>
        <textarea
          value={form.comment}
          onChange={(e) => setForm({ ...form, comment: e.target.value })}
          rows={2}
          disabled={posted}
        />
      </div>

      {error && (
        <div className="error" style={{ marginTop: '1rem', color: 'var(--badge-red-fg)' }}>
          {error}
        </div>
      )}
    </div>
  );
}
